import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Star, Check, X, Trash2, Eye } from "lucide-react";

const STATUS = {
  pending: { label: "Pendiente", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  published: { label: "Publicada", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { label: "Descartada", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

export const ReviewsAdminModal = ({ onClose, onChanged }) => {
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/reviews")
      .then((r) => setReviews(r.data.reviews || []))
      .catch(() => toast.error("No se pudieron cargar las reseñas"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const setStatus = async (id, status) => {
    setBusy(id);
    try {
      await api.post(`/admin/reviews/${id}/status`, { status });
      toast.success(status === "published" ? "Publicada en la web" : "Actualizada");
      load();
      onChanged?.();
    } catch {
      toast.error("No se pudo actualizar");
    } finally {
      setBusy(null);
    }
  };

  const remove = async (id) => {
    setBusy(id);
    try {
      await api.delete(`/admin/reviews/${id}`);
      load();
      onChanged?.();
    } catch {
      toast.error("No se pudo borrar");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-950/50 z-50 flex items-start sm:items-center justify-center p-3 sm:p-8"
         onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
           onClick={(e) => e.stopPropagation()} data-testid="reviews-admin-modal">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Web pública</div>
            <h3 className="font-display text-xl font-bold text-slate-900">Reseñas de clientes</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900" data-testid="reviews-admin-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 space-y-3">
          {loading && <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</div>}
          {!loading && reviews.length === 0 && (
            <p className="text-sm text-slate-500">
              Todavía no hay reseñas. Pide una desde el botón de cada licencia y aparecerán aquí para que decidas si se publican.
            </p>
          )}
          {reviews.map((r) => {
            const st = STATUS[r.status] || STATUS.pending;
            return (
              <div key={r.id} className="border border-slate-200 rounded-xl p-4" data-testid={`review-row-${r.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex items-center gap-0.5">
                    {Array.from({ length: r.rating || 0 }).map((_, i) => (
                      <Star key={i} className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />
                    ))}
                  </div>
                  <span className="font-bold text-sm text-slate-900">{r.pharmacy_name}</span>
                  <span className="text-xs text-slate-500">{r.city}</span>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${st.cls}`}>{st.label}</span>
                  {r.license_code && <span className="text-[10px] font-mono-num text-slate-400">{r.license_code}</span>}
                </div>
                <p className="text-sm text-slate-700 mt-2 leading-relaxed">“{r.text}”</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {r.status !== "published" && (
                    <button onClick={() => setStatus(r.id, "published")} disabled={busy === r.id}
                            className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-1.5 rounded-md"
                            data-testid={`review-publish-${r.id}`}>
                      <Check className="h-3.5 w-3.5" /> Publicar en la web
                    </button>
                  )}
                  {r.status === "published" && (
                    <button onClick={() => setStatus(r.id, "pending")} disabled={busy === r.id}
                            className="inline-flex items-center gap-1.5 border border-slate-300 text-slate-700 text-xs font-bold px-3 py-1.5 rounded-md hover:bg-slate-50"
                            data-testid={`review-unpublish-${r.id}`}>
                      <Eye className="h-3.5 w-3.5" /> Quitar de la web
                    </button>
                  )}
                  {r.status !== "rejected" && (
                    <button onClick={() => setStatus(r.id, "rejected")} disabled={busy === r.id}
                            className="text-xs font-bold text-slate-500 hover:text-slate-900 px-2 py-1.5"
                            data-testid={`review-reject-${r.id}`}>
                      Descartar
                    </button>
                  )}
                  <button onClick={() => remove(r.id)} disabled={busy === r.id}
                          className="ml-auto text-slate-400 hover:text-rose-600 p-1.5"
                          title="Borrar" data-testid={`review-delete-${r.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
